import { setLocale } from '@thoughtspot/date-format-util';
import { updateStrings as importedUpdateStrings } from '@thoughtspot/translations';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-xhr-backend';
import _ from 'lodash';
import { initReactI18next, useTranslation as i18nUseTranslation, } from 'react-i18next';
import { DEFAULT_NAMESPACE, fallbackLng, getCurrentLocale, getPreferredLocale, localeIdFromResourceName, localeMaster, setAppLocaleInitialised, setPreferredLocaleGetter, supportedLocalesMap, } from './i18n';
import { getNewLocaleId } from './numbers';
import { getStringsTranslationsPath, resourceLoader, updateStringsForEmbed, updateTranslationOverridesWithKeys, } from './util';
let stringOverrides = null;
let translationIDOverrides = null;
export let languageDetector = null;
const getLocalesToDisplay = () => {
    const displayLocales = [];
    _.forEach(localeMaster, locale => {
        if (locale.displayName) {
            const { id, displayName: name } = locale;
            displayLocales.push({
                id,
                name,
            });
        }
    });
    return displayLocales;
};
export const getBrowserLocale = () => {
    const clientLanguages = languageDetector?.detect(['navigator']);
    const supportedClientLanguageId = clientLanguages?.find(lng => supportedLocalesMap[lng] !== undefined);
    return (localeIdFromResourceName[supportedLocalesMap[supportedClientLanguageId]] ?? fallbackLng);
};
export const getPreferredLocaleID = () => {
    const overrideLocaleId = getBrowserLocale();
    return getPreferredLocale(null, null, overrideLocaleId);
};
export const getClientLocale = () => {
    const preferredLocaleID = getPreferredLocaleID();
    return supportedLocalesMap[preferredLocaleID];
};
export const initializeI18N = () => {
    if (!i18n.isInitialized) {
        languageDetector = new LanguageDetector();
        languageDetector.addDetector({
            name: 'appLanguageDetector',
            lookup: () => getClientLocale(),
        });
        i18n.use(Backend)
            .use(languageDetector)
            .use(initReactI18next)
            .init({
            detection: {
                order: ['querystring', 'appLanguageDetector'],
                lookupQuerystring: 'locale',
            },
            debug: false,
            backend: {
                loadPath: (lngs) => {
                    const locale = _.head(lngs);
                    return getStringsTranslationsPath(locale);
                },
                ajax: async (url, options, callback) => {
                    callback(await resourceLoader(url), {});
                },
                parse: data => data,
                crossDomain: true,
            },
            fallbackLng: [supportedLocalesMap[fallbackLng]],
            interpolation: {
                escapeValue: false,
                prefix: '{',
                suffix: '}',
                skipOnVariables: true,
            },
            react: {
                useSuspense: false,
            },
            lowerCaseLng: true,
            defaultNS: DEFAULT_NAMESPACE,
            whitelist: _.uniq(_.values(supportedLocalesMap)),
        });
    }
};
let allowExposedTranslate = false;
const exposedTranslate = (i18nT, key, args) => {
    if (!key) {
        return i18nT(key);
    }
    let argsString = '';
    if (!_.isUndefined(args) && !_.isEmpty(args)) {
        argsString = _.isArray(args) ? args.join(', ') : JSON.stringify(args);
    }
    return `|| ${i18nT(key)} [${key}]${argsString} ||`;
};
let translationCache = new WeakMap();
const getTranslationFunction = (i18nT) => {
    let cachedFunction = translationCache.get(i18nT);
    if (!cachedFunction) {
        cachedFunction = allowExposedTranslate
            ? (key, args) => exposedTranslate(i18nT, key, args)
            : i18nT;
        translationCache.set(i18nT, cachedFunction);
    }
    return cachedFunction;
};
const clearTranslationCache = () => {
    translationCache = new WeakMap();
};
export const useTranslation = function () {
    const { t: i18nT, ...rest } = i18nUseTranslation();
    return {
        t: getTranslationFunction(i18nT),
        ...rest,
    };
};
export function setStringOverrides(overrides) {
    stringOverrides = overrides;
}
export function setTranslationIDOverrides(translationIDOverridesObject) {
    translationIDOverrides = translationIDOverridesObject;
}
export function overrideStrings(overrides, translationIDOverridesObject) {
    if (overrides) {
        stringOverrides = overrides;
    }
    if (translationIDOverridesObject) {
        translationIDOverrides = translationIDOverridesObject;
    }
    if (!i18n.isInitialized) {
        i18n.on('initialized', () => {
            overrideStrings(stringOverrides, translationIDOverrides);
        });
        return;
    }
    const stringBundle = i18n.getResourceBundle(getCurrentLocale(), DEFAULT_NAMESPACE);
    const updatedStringBundle = updateStringsForEmbed(stringBundle, stringOverrides);
    const updatedStringIDOverrides = updateTranslationOverridesWithKeys(updatedStringBundle, translationIDOverrides);
    importedUpdateStrings(updatedStringIDOverrides);
    i18n.addResourceBundle(getCurrentLocale(), DEFAULT_NAMESPACE, updatedStringIDOverrides, true, true);
}
export function isCurrentLanguageEnglish() {
    return getCurrentLocale().startsWith('en');
}
export const initializeAppLocale = async (getFormatLocales, shouldAllowExposedTranslate = false) => {
    const getStandardizedPreferredLocale = (clientLocale, clusterLocalizationEnabled, browserLocale) => {
        return getFormatLocales(clientLocale, clusterLocalizationEnabled, browserLocale).preferredLocale.replace('_', '-');
    };
    const getStandardizedPreferredDateLocale = (clientLocale) => {
        const { dateFormatLocale } = getFormatLocales(clientLocale);
        return getNewLocaleId(dateFormatLocale.replace('_', '-'));
    };
    allowExposedTranslate = !!shouldAllowExposedTranslate;
    clearTranslationCache();
    initializeI18N();
    setPreferredLocaleGetter(getStandardizedPreferredLocale);
    setLocale(getStandardizedPreferredDateLocale());
    setAppLocaleInitialised();
};
export const getAppLocale = () => {
    const currentLocale = getCurrentLocale().toLowerCase();
    const matchedLocale = _.map(supportedLocalesMap, (loc, key) => {
        if (currentLocale === loc) {
            return key;
        }
        return '';
    }).filter(i => i)[0] || fallbackLng;
    return matchedLocale;
};
export const localesToDisplay = getLocalesToDisplay();
export const translate = (key, args) => {
    return allowExposedTranslate
        ? exposedTranslate(i18n.t.bind(i18n), key, args)
        : i18n.t(key, args);
};
export const translateLazy = (key, args) => () => translate(key, args);
export const translateWithInterpolation = (key, ...rest) => translate(key, ...rest);
export default i18n;
export * from './numbers';
export { initializeMobileAppLocale } from './index-mobile';
export { getStringsTranslationsPath, getNumbersTranslationsPath, getDateFormatsTranslationsPath, } from './util';
export { getCurrentLocale, setPreferredLocaleGetter, fallbackLng, } from './i18n';
window.i18n = window.i18n || i18n;
//# sourceMappingURL=index.js.map